"use client";

import Image, { StaticImageData } from "next/image";
import { Title } from "@/components/common/typography/Title";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { MdiChevronLeft } from "@/assets/icons/mdi-chevron-left";
import { MdiChevronRight } from "@/assets/icons/mdi-chevron-right";
import { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import type { Offer } from "@/data/offers";
import {
  refreshContactAnchorIfNeeded,
  saveContactIntent,
} from "@/lib/contact-intent";

interface IntroSectionProps {
  offer: Offer;
}

const ImageCarousel = ({
  images,
  className,
}: {
  images: StaticImageData[];
  className?: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(0);
  const [controlsHidden, setControlsHidden] = useState(false);
  const [progress, setProgress] = useState(0);

  const nextImage = useCallback(() => {
    if (!images.length) return;
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setProgress(0);
  }, [images.length]);

  const previousImage = () => {
    if (!images.length) return;
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setProgress(0);
  };

  const handleDragEnd = (_event: unknown, info: { offset: { x: number } }) => {
    const dragDistance = info.offset.x;
    const dragThreshold = 50;

    if (Math.abs(dragDistance) > dragThreshold) {
      if (dragDistance > 0) {
        previousImage();
      } else {
        nextImage();
      }
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let progressInterval: NodeJS.Timeout;

    if (!isPaused && images.length > 1) {
      interval = setInterval(nextImage, 4000);

      // Mise à jour de la barre de progression toutes les 100 ms.
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) return 0;
          return prev + (100 / 4000) * 100;
        });
      }, 100);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isPaused, nextImage, images.length]);

  useEffect(() => {
    setCurrentIndex(0);
    setProgress(0);
    setDirection(0);
  }, [images]);

  const safeIndex = images[currentIndex] ? currentIndex : 0;
  const activeImage = images[safeIndex];

  if (!activeImage) return null;

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir: number) => ({
      zIndex: 0,
      x: dir < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
    }),
  };

  return (
    <div className="group relative overflow-hidden">
      <button
        onClick={() => setControlsHidden(!controlsHidden)}
        className="pointer-events-auto absolute right-4 top-4 z-10 block rounded-full border border-primary bg-white/80 p-2 text-primary transition-all duration-200 hover:bg-primary hover:text-white lg:hidden"
        title={
          controlsHidden ? "Afficher les contrôles" : "Masquer les contrôles"
        }
      >
        <Icon
          icon={controlsHidden ? "mdi:eye" : "mdi:eye-off"}
          className="h-6 w-6"
        />
      </button>

      <div className="absolute bottom-0 left-0 z-20 h-1 w-full bg-white/20">
        <div
          className="h-full bg-primary transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className={`relative h-[400px] w-full ${className}`}>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={safeIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={handleDragEnd}
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
              scale: { duration: 0.2 },
            }}
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
          >
            <Image
              src={activeImage}
              alt={`Image ${safeIndex + 1}`}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="rounded-lg object-cover"
              priority={safeIndex === 0}
              draggable={false}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <button
        onClick={() => setIsPaused(!isPaused)}
        className={`pointer-events-auto absolute bottom-4 right-4 z-10 rounded-full border border-primary bg-white/80 p-2 text-primary opacity-100 transition-all duration-200 hover:bg-primary hover:text-white ${
          controlsHidden ? "opacity-0" : "opacity-100"
        }`}
        title={isPaused ? "Reprendre le défilement" : "Mettre en pause"}
      >
        <Icon icon={isPaused ? "mdi:play" : "mdi:pause"} className="h-6 w-6" />
      </button>

      <div
        className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-between p-4 ${
          controlsHidden ? "opacity-0" : "opacity-100"
        } lg:opacity-0 lg:transition-opacity lg:duration-200 lg:group-hover:opacity-100`}
      >
        <button
          onClick={previousImage}
          className="pointer-events-auto rounded-full border border-primary bg-white/80 p-2 text-primary transition-colors hover:bg-primary hover:text-white"
          title="Image précédente"
        >
          <MdiChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={nextImage}
          className="pointer-events-auto rounded-full border border-primary bg-white/80 p-2 text-primary transition-colors hover:bg-primary hover:text-white"
          title="Image suivante"
        >
          <MdiChevronRight className="h-6 w-6" />
        </button>
      </div>

      <div
        className={`pointer-events-none absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2 ${
          controlsHidden ? "opacity-0" : "opacity-100"
        } lg:opacity-0 lg:transition-opacity lg:duration-200 lg:group-hover:opacity-100`}
      >
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`pointer-events-auto h-2 w-2 rounded-full transition-all ${
              safeIndex === index
                ? "bg-primary"
                : "bg-white/50 hover:bg-white/80"
            }`}
            title={`Aller à l'image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const IntroSection = ({ offer }: IntroSectionProps) => {
  return (
    <section className="flex flex-col gap-12 px-4 pb-10 pt-20 md:px-24 lg:px-48 xl:px-96">
      <Title title={offer.name} />
      <div className="flex flex-col gap-8 text-black lg:flex-row">
        <div className="relative lg:w-1/2">
          <ImageCarousel images={offer.photos} />
        </div>
        <div className="flex flex-col gap-8 lg:w-1/2">
          <p className="text-lg">{offer.longDescription}</p>
          <PrimaryButton
            text="Nous contacter"
            navigateTo="#contact"
            onClick={() => {
              saveContactIntent({
                offerType: offer.contactOfferType,
                source: `offres-${offer.id}`,
              });
              refreshContactAnchorIfNeeded();
            }}
          />
        </div>
      </div>
    </section>
  );
};
